import { Box, Grid, Card, CardContent, Typography, Chip, Avatar, MenuItem, TextField, CircularProgress } from "@mui/material";
import MaterialTable from "material-table";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import { useState, useEffect } from "react";
import CustomToggle from "../../../Base/views/CustomToggle";
import { GetMonthlyReportAction, GetReportRecruiterAction } from "../../../../../store/ATS/Report/Action";
import { useDispatch, useSelector } from "react-redux";
import Chart from "react-apexcharts";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// ─── Brand ─────────────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatINR = (val) => {
    const n = Number(val) || 0;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toLocaleString("en-IN")}`;
};

const getStatusStyle = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("received") || s.includes("paid")) return { bg: "#D1FAE5", color: "#059669" };
    if (s.includes("pending")) return { bg: "#FEF3C7", color: "#D97706" };
    if (s.includes("outstanding") || s.includes("due")) return { bg: "#FEE2E2", color: "#DC2626" };
    return { bg: "#F1F5F9", color: "#64748B" };
};

// ─── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, color }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 40, height: 40 }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} fontSize="1.3rem" lineHeight={1}>{value}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const MonthlyClosuresNewReport = () => {
    const dispatch = useDispatch();
    const [localIsDashboardView, setLocalIsDashboardView] = useState(true);
    const now = new Date();
    const [userData, setUserData] = useState({ load: true, values: [] });
    const [filters, setFilters] = useState({
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        primary_recruiter_filter: "",
        secondary_recruiter_filter: "",
    });

    const { atsMonthlyReport, isMonthlyRepLoading } = useSelector(state => state.AtsReducer?.AtsReportReducer);
    const { atsRecruiterData } = useSelector(state => state.AtsReducer?.AtsReportReducer);

    // ── Load recruiters once ──────────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetReportRecruiterAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (atsRecruiterData?.length > 0) {
            setUserData({ load: false, values: atsRecruiterData });
        }
    }, [atsRecruiterData]);

    // ── Fetch monthly data on filter change ───────────────────────────────────
    useEffect(() => {
        dispatch(GetMonthlyReportAction({
            month: filters.month,
            year: filters.year,
            primary_recruiter: filters.primary_recruiter_filter
                ? `{${filters.primary_recruiter_filter.ats_users_id}}`
                : null,
            secondary_recruiter: filters.secondary_recruiter_filter
                ? `{${filters.secondary_recruiter_filter.ats_users_id}}`
                : null,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.month, filters.year, filters.primary_recruiter_filter, filters.secondary_recruiter_filter]);

    const handleFilter = (event, value) => {
        if (["primary_recruiter_filter", "secondary_recruiter_filter"].includes(event)) {
            setFilters(f => ({ ...f, [event]: value || "" }));
        }
        if (event?.target?.name === "month") setFilters(f => ({ ...f, month: Number(event.target.value) }));
        if (event?.target?.name === "year") setFilters(f => ({ ...f, year: Number(event.target.value) }));
    };

    // ── Data ──────────────────────────────────────────────────────────────────
    const closures = atsMonthlyReport || [];

    // KPIs
    const totalJoinings = closures?.length || 0;
    const totalBilling = closures?.reduce((s, r) => s + (Number(r.billing_amount) || 0), 0) || 0;
    const totalReceived = closures?.reduce((s, r) => s + (Number(r.received_amount) || 0), 0) || 0;

    // Payment status donut
    const statusCounts = {};
    closures?.forEach(c => {
        const key = c.payment_status || "Unknown";
        statusCounts[key] = (statusCounts[key] || 0) + 1;
    });
    const statusLabels = Object.keys(statusCounts);
    const donutChart = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: statusLabels.length ? statusLabels : ["No Data"],
            colors: ["#10B981", "#F59E0B", "#EF4444", "#94A3B8"].slice(0, statusLabels.length || 1),
            dataLabels: { enabled: false },
            stroke: { width: 0 },
            legend: { position: "bottom", fontSize: "11px", markers: { radius: 3 } },
            plotOptions: {
                pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Closures", fontSize: "12px", color: "#94A3B8" } } } },
            },
        },
        series: statusLabels.length ? statusLabels.map(s => statusCounts[s]) : [1],
    };

    // Billing by recruiter horizontal bar
    const recruiterBilling = {};
    closures?.forEach(c => {
        const name = c.recruiter_name || "Unknown";
        recruiterBilling[name] = (recruiterBilling[name] || 0) + Number(c.billing_amount || 0);
    });
    const recruiterEntries = Object.entries(recruiterBilling)?.sort((a, b) => b[1] - a[1]);
    const billingBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND],
            plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
            dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 600 }, formatter: v => formatINR(v) },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" }, formatter: v => formatINR(v) } },
            yaxis: { labels: { style: { fontSize: "11px", fontWeight: 500 } } },
        },
        series: [{ name: "Billing", data: recruiterEntries.map(([name, val]) => ({ x: name.split(" ")[0], y: val })) }],
    };

    // Billing vs Received grouped bar (top 8 closures)
    const top8 = closures.slice(0, 8);
    const groupedBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND, "#10B981"],
            plotOptions: { bar: { columnWidth: "50%", borderRadius: 4 } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: {
                categories: top8.map(c => (c.candidate_name || "").split(" ")[0]),
                labels: { style: { fontSize: "10px", fontWeight: 500 }, rotate: -30 },
            },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" }, formatter: v => formatINR(v) } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Billing", data: top8.map(c => Number(c.billing_amount || 0)) },
            { name: "Received", data: top8.map(c => Number(c.received_amount || 0)) },
        ],
    };

    // Top billers
    const topBillers = [...recruiterEntries].slice(0, 5);
    const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

    // Year options
    const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i);

    // Table columns
    const columns = [
        {
            title: "Candidate", field: "candidate_name",
            cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" },
        },
        {
            title: "Client", field: "client_name",
            cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" },
        },
        {
            title: "Designation", field: "designation",
            cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" },
        },
        {
            title: "Recruiter", field: "recruiter_name",
            cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" },
        },
        {
            title: "Billing", field: "billing_amount", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "right" }, headerStyle: { padding: "10px", textAlign: "right" },
            render: r => <Typography fontWeight={600} fontSize="0.82rem" color={BRAND}>{formatINR(r.billing_amount)}</Typography>,
        },
        {
            title: "Received", field: "received_amount", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "right" }, headerStyle: { padding: "10px", textAlign: "right" },
            render: r => <Typography fontWeight={600} fontSize="0.82rem" color="#10B981">{formatINR(r.received_amount)}</Typography>,
        },
        {
            title: "Payment Status", field: "payment_status",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => {
                const s = getStatusStyle(r.payment_status);
                return <Chip label={r.payment_status || "Unknown"} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: "0.7rem" }} />;
            },
        },
    ];

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters & View Toggle ─────────────────────────────── */}
                <Grid container columnSpacing={1} rowSpacing={1} mb={2} alignItems="center">
                    <Grid item xs={6} sm={2.5} md={1.5}>
                        <TextField
                            select fullWidth size="small" label="Month" name="month"
                            value={filters.month}
                            onChange={e => handleFilter(e)}
                        >
                            {MONTHS?.map((m, i) => (
                                <MenuItem key={m} value={i + 1}>{m}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={2} md={1.5}>
                        <TextField
                            select fullWidth size="small" label="Year" name="year"
                            value={filters.year}
                            onChange={e => handleFilter(e)}
                        >
                            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3.5} md={2.5}> 
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => o?.ats_users_id === v?.ats_users_id}
                            label="Primary Recruiter"
                            value={filters.primary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name"
                            required={false}
                            variant="outlined"
                            name="primary_recruiter_filter"
                            onChangeEvent={handleFilter}
                            data={userData}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3.5} md={2.5}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => o?.ats_users_id === v?.ats_users_id}
                            label="Secondary Recruiter"
                            value={filters.secondary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name"
                            required={false}
                            variant="outlined"
                            name="secondary_recruiter_filter"
                            onChangeEvent={handleFilter}
                            data={userData}
                            size="small"
                        />
                    </Grid>
                    {isMonthlyRepLoading && (
                        <Grid item xs="auto" sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress size={22} disableShrink/>
                        </Grid>
                    )}
                    <Grid item xs sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
                        {setLocalIsDashboardView && (
                            <CustomToggle
                                isChecked={!localIsDashboardView}
                                handleToggleChange={() => setLocalIsDashboardView(!localIsDashboardView)}
                                Yestext={"Table"}
                                NoText={"Dashboard"}
                                YesTooltip={"Switch to Table View"}
                                NoTooltip={"Switch to Dashboard View"}
                                style={{ height: "37px", labelPadding: "2px 8px" }}
                            />
                        )}
                    </Grid>
                </Grid>

                {localIsDashboardView ? (
                    <>
                        {/* ── KPI Cards ─────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={4}>
                                <KpiCard icon={<PersonOutlineIcon sx={{ fontSize: 20 }} />} value={totalJoinings} label="Joinings / Closures" color="#4E79A7" />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <KpiCard icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />} value={formatINR(totalBilling)} label="Total Billing" color={BRAND} />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <KpiCard icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />} value={formatINR(totalReceived)} label="Total Received" color="#10B981" />
                            </Grid>
                        </Grid>

                        {/* ── Charts Row ────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Payment Status</Typography>
                                        <Typography variant="caption" color="text.secondary">By status type</Typography>
                                        {statusLabels.length > 0 ? (
                                            <Chart options={donutChart.options} series={donutChart.series} type="donut" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for selected month</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Billing by Recruiter</Typography>
                                        <Typography variant="caption" color="text.secondary">Revenue contribution per recruiter</Typography>
                                        {recruiterEntries.length > 0 ? (
                                            <Chart options={billingBar.options} series={billingBar.series} type="bar" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for selected month</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Billing vs Received</Typography>
                                        <Typography variant="caption" color="text.secondary">Per closure comparison (top 8)</Typography>
                                        {top8.length > 0 ? (
                                            <Chart options={groupedBar.options} series={groupedBar.series} type="bar" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for selected month</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* ── Top Billers ───────────────────────────────────────── */}
                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <Card>
                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                        <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.5, color: "#FECB03", verticalAlign: "text-bottom" }} />
                                        Top Billers
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {topBillers?.map(([name, billing], i) => (
                                            <Grid item xs={12} sm={6} md={2.4} key={name}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, p: 1.2, bgcolor: "#ffffff", borderRadius: 1.5 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: i < 3 ? `${medalColors[i]}20` : "#F1F5F9", color: i < 3 ? medalColors[i] : "#94A3B8", fontSize: "0.75rem", fontWeight: 800 }}>
                                                        #{i + 1}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={600} fontSize="0.8rem" noWrap>{name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{formatINR(billing)} billed</Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    {closures?.length === 0 && (
                                        <Typography fontSize="0.8rem" color="text.secondary">No data</Typography>
                                    )}
                                </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                    </>
                ) : (
                    /* ── Table Grid View ────────────────────────────────────────── */
                    <Box sx={{ mt: 1 }}>
                        <MaterialTable
                            columns={columns}
                            data={closures}
                            options={{
                                maxBodyHeight: "calc(100vh - 280px)",
                                minBodyHeight: "350px",
                                sorting: true,
                                search: true,
                                searchFieldAlignment: "right",
                                searchAutoFocus: true,
                                searchFieldVariant: "standard",
                                filtering: false,
                                exportButton: true,
                                exportAllData: true,
                                paging: true,
                                pageSizeOptions: [25, 50, 100],
                                pageSize: 25,
                                actionsColumnIndex: -1,
                                draggable: false,
                                columnsButton: true,
                                headerStyle: {
                                    backgroundColor: "#fff",
                                    color: "#64748B",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    whiteSpace: "nowrap",
                                    padding: "10px",
                                },
                            }}
                            title={`Monthly Closures — ${MONTHS[filters.month - 1]} ${filters.year}`}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default MonthlyClosuresNewReport;
